import { ApiRequestMeta, useRestApiProvider } from "@jugl-web/rest-api";
import { getFileFromURL } from "@jugl-web/utils/utils/files";
import { useCallback, useMemo } from "react";
import { LiveChatAppSettings } from "../types";
import { getDefaultCTAVisual } from "../utils/defaultCTAVisuals";
import { resolveLiveChatAppSettings } from "../utils/resolveLiveChatAppSettings";

const FILE_STORAGE_BASE_URL =
  "https://jugl-chatbot-data.s3.eu-west-3.amazonaws.com";

type UpdateLiveChatAppSettingsPayload = Partial<
  Omit<
    LiveChatAppSettings,
    "faviconUrl" | "businessLogoUrl" | "agentImage" | "ctaVisual"
  > & {
    faviconUrl: File | string | null;
    businessLogoUrl: File | string | null;
    agentImage: File | string | null;
    ctaVisual: File | string | null;
  }
>;

interface UseUpdateLiveChatAppSettingsOptions {
  entityId: string;
}

type LiveChatImageFileType = "launcher_icon" | "avatar_icon" | "chat_icon";

type FileToUpload =
  | { type: LiveChatImageFileType; file: File }
  | { type: "welcome_msg_media"; id: string; file: File };

const liveChatImageFileTypeToSettingsKey: Record<
  LiveChatImageFileType,
  keyof LiveChatAppSettings
> = {
  launcher_icon: "businessLogoUrl",
  avatar_icon: "agentImage",
  chat_icon: "faviconUrl",
};

export const useUpdateLiveChatAppSettings = ({
  entityId,
}: UseUpdateLiveChatAppSettingsOptions) => {
  const { integrationsApi } = useRestApiProvider();

  const { data, isLoading } = integrationsApi.useGetLiveChatAppConfigQuery({
    entityId,
  });

  const [updateLiveChatAppConfig, { isLoading: isLiveChatAppSettingsSaving }] =
    integrationsApi.useUpdateLiveChatAppConfigMutation();

  const [uploadLiveChatAppFiles] =
    integrationsApi.useUploadLiveChatAppFilesMutation();

  const currentSettings = useMemo<LiveChatAppSettings>(
    () => resolveLiveChatAppSettings(data?.settings),
    [data?.settings]
  );

  const updateLiveChatAppSettings = useCallback(
    async (
      payload: UpdateLiveChatAppSettingsPayload,
      meta?: ApiRequestMeta
    ) => {
      if (isLoading) {
        return;
      }

      const { faviconUrl, businessLogoUrl, agentImage, ctaVisual, ...rest } =
        payload;

      const updatedSettings: LiveChatAppSettings = {
        ...currentSettings,
        ...rest,
      };

      const filesToUpload: FileToUpload[] = [];

      if (typeof faviconUrl !== "undefined") {
        if (faviconUrl instanceof File) {
          filesToUpload.push({
            type: "chat_icon",
            file: faviconUrl,
          });
        } else {
          updatedSettings.faviconUrl = faviconUrl ?? "";
        }
      }

      if (typeof businessLogoUrl !== "undefined") {
        if (businessLogoUrl instanceof File) {
          filesToUpload.push({
            type: "launcher_icon",
            file: businessLogoUrl,
          });
        } else {
          updatedSettings.businessLogoUrl = businessLogoUrl ?? "";
        }
      }

      if (typeof agentImage !== "undefined") {
        if (agentImage instanceof File) {
          filesToUpload.push({
            type: "avatar_icon",
            file: agentImage,
          });
        } else {
          updatedSettings.agentImage = agentImage ?? "";
        }
      }

      if (typeof ctaVisual !== "undefined") {
        if (ctaVisual === null) {
          updatedSettings.ctaVisual = null;
        } else if (typeof ctaVisual === "string") {
          if (
            ctaVisual.startsWith("default") &&
            currentSettings.ctaVisual?.id !== ctaVisual
          ) {
            const defaultVisual = getDefaultCTAVisual(ctaVisual);

            const file = await getFileFromURL(
              defaultVisual.src,
              `${defaultVisual.id}.webm`,
              "video/webm"
            );

            filesToUpload.push({
              type: "welcome_msg_media",
              id: defaultVisual.id,
              file,
            });
          }
        } else if (ctaVisual instanceof File) {
          filesToUpload.push({
            type: "welcome_msg_media",
            id: "custom",
            file: ctaVisual,
          });
        }
      }

      if (filesToUpload.length > 0) {
        const response = await uploadLiveChatAppFiles({
          entityId,
          files: filesToUpload,
          // Error handling is shared, but the success toast belongs to the
          // config update below — otherwise saving with a new image toasts twice
          meta: { ...meta, successMessage: undefined },
        });

        if (!("data" in response)) {
          throw new Error("Uploading live chat app files failed");
        }

        filesToUpload.forEach((entry) => {
          const fileName = entry.type;
          const fileExtension = entry.file.name.split(".").pop();
          const hash = crypto.randomUUID().substring(0, 5);
          const fileUrl = `${FILE_STORAGE_BASE_URL}/${entityId}/livechat_files/${fileName}.${fileExtension}?v=${hash}`;

          if (entry.type === "welcome_msg_media") {
            updatedSettings.ctaVisual = { id: entry.id, url: fileUrl };
          } else {
            (updatedSettings[
              liveChatImageFileTypeToSettingsKey[entry.type]
            ] as string) = fileUrl;
          }
        });
      }

      await updateLiveChatAppConfig({
        entityId,
        settings: {
          ...currentSettings,
          ...rest,
          ...updatedSettings,
        },
        meta,
      }).unwrap();
    },
    [
      currentSettings,
      entityId,
      isLoading,
      updateLiveChatAppConfig,
      uploadLiveChatAppFiles,
    ]
  );

  return {
    liveChatAppAccount: data,
    updateLiveChatAppSettings,
    isLiveChatAppSettingsSaving,
  };
};
